/**
 * The permission and feature keys screens gate on **outside** the nav table
 * and the action registry.
 *
 * `nav.ts` and `actionRegistry.ts` already hold their keys in positions the
 * compiler checks, but a screen gating a *section* (the usage panel on SIM
 * detail, a write control on a read screen) has neither table to lean on and
 * would otherwise reach for `hasPermission('sims.usage.read')` — the bare
 * string criterion 13 outlaws, because a key renamed in the contract would
 * silently un-gate it rather than break the build.
 *
 * `satisfies Record<string, PermissionKey>` keeps both guarantees at once:
 * the values are compiler-checked against the catalog (a rename here is a
 * build error), and `as const` keeps each entry's literal type so callers
 * stay exact. This file is on `sourceScan.spec.ts`'s typed-declaration
 * allow-list for exactly this reason — add keys here, never inline.
 */

import type { FeatureKey, PermissionKey } from '@rd/shared';

export const PERMISSION = {
  usersWrite: 'users.write',
  brandingWrite: 'branding.write',
  usageRead: 'sims.usage.read',
  // §B4/PR-8: gates the reveal eye-toggle and the action flows' full-identifier
  // fetches. Granted to TENANT_ADMIN and TENANT_OPERATOR in the catalog —
  // without it lifecycle payloads could only carry masked values.
  identifiersReveal: 'sims.identifiers.reveal',
} as const satisfies Record<string, PermissionKey>;

export const FEATURE = {
  usage: 'sims.usage',
} as const satisfies Record<string, FeatureKey>;

/**
 * The feature that has to be on for a tenant to CREATE each kind of job.
 *
 * The jobs screen filters by type, and offering a type the account cannot
 * start is a control that can only ever return nothing — an estate entitled to
 * activate and swap alone was being offered nine job families to filter by.
 * Deriving the list from entitlement keeps it honest as features are turned on
 * or off, instead of a hand-maintained list drifting from what the tenant has.
 *
 * Entitlement is one half of that filter. The other is what `GET /jobs` will
 * actually list, which is narrower still and is the server's to decide —
 * `JobListPage` intersects the two.
 */
export const JOB_TYPE_FEATURE = {
  SIM_ACTIVATE: 'sims.lifecycle.activate',
  SIM_ACTIVATE_TEST: 'sims.lifecycle.test_mode',
  // The route that creates this job is guarded by the *activate* feature
  // (`POST /sims/activate-from-test`), not by test-mode: a tenant whose test
  // entitlement was withdrawn can still move its test SIMs onto a live plan,
  // and gating on test_mode described a rule the API does not enforce.
  SIM_ACTIVATE_FROM_TEST: 'sims.lifecycle.activate',
  SIM_SWAP: 'sims.lifecycle.swap',
  SIM_SUSPEND: 'sims.lifecycle.safe_custody',
  SIM_RESUME: 'sims.lifecycle.safe_custody',
  SIM_TEMP_DISCONNECT: 'sims.lifecycle.disconnect',
  SIM_TD_RESUME: 'sims.lifecycle.disconnect',
  SIM_CHANGE_PLAN: 'sims.plan_upgrade',
} as const satisfies Record<string, FeatureKey>;
